/**
 * Shared elicitation-card answer controls — ONE implementation, reused by BOTH the
 * workshop-runner (`career-workshop-runner`) and the Werte-Interview
 * (`career-values-interview`), rather than each surface reinventing them. The
 * surfaces own their PRESENTATION (the runner's timeline, the interview's Fraunces
 * prose header); the ANSWER inputs come from here.
 *
 * Shared: every answer control — the `.opt`/`.cell` reaction grids (pairwise /
 * best_worst / vignette / simulation), the semantic-differential slider, and the
 * open-text field. All are SELECT-then-CONFIRM (operator ruling): a pick highlights,
 * a visible confirm folds it; Enter or a second click on the already-selected option
 * is the fast-path confirm (wired per surface). Each surface adds its OWN confirm
 * button + any extra chrome (the runner's »nicht sicher«/skip); the grids here emit
 * `pick-option` / `toggle-veto` actions stamped with `data-block` so a surface that
 * shows several cards at once (the interview's batch round) can route each pick.
 * Everything renders into LIGHT DOM, so the `.opt`/`.cell`/`.sd-*`/`.rlhf-seam`
 * styles in `index.html` reach them directly.
 */

import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow } from "@platform/rlhf-elements"

export interface DifferentialAxis {
  left: string
  right: string
}

/**
 * The semantic-differential slider — the shared answer control for a
 * `semantic_differential` card. Redesign (operator dogfood: the old 3-column
 * `[pole][range][pole]` grid squeezed the range to near-zero width): the pole
 * labels sit ABOVE, left/right-aligned; the range spans the FULL width beneath them
 * with a generous hit area and a visible, theme-aware track + thumb. Keyboard is
 * native (←/→ step the value; Home/End jump to the poles). `.sd-range` is the read
 * hook — see {@link readDifferentialValue}.
 */
export function semanticDifferential(axis: DifferentialAxis | undefined, ariaLabel: string): TemplateResult {
  return html`
    <div class="sd-field">
      <div class="sd-poles">
        <span class="sd-pole sd-pole-left" lang="de">${axis?.left ?? ""}</span>
        <span class="sd-pole sd-pole-right" lang="de">${axis?.right ?? ""}</span>
      </div>
      <input class="sd-range" type="range" min="0" max="1" step="0.01" value="0.5" aria-label=${ariaLabel} />
    </div>`
}

/** Read a `.sd-range` value from a container (0..1; defaults to the 0.5 midpoint). */
export function readDifferentialValue(root: Element | null | undefined): number {
  const raw = root?.querySelector<HTMLInputElement>("input.sd-range")?.value
  const n = raw != null ? Number(raw) : NaN
  return Number.isFinite(n) ? n : 0.5
}

/**
 * The open-text answer field — the reusable seamless input (`@platform/rlhf-elements`
 * `.rlhf-seam`: box-less, underline-only, auto-growing via `seamAutoGrow()`). Shared
 * by the interview's open-narrative body and the runner's `why_ladder`/`open_narrative`
 * steps so both use ONE seamless control. `.et-answer` is the read hook — see
 * {@link readOpenText}.
 */
export function openTextField(placeholder: string, ariaLabel: string): TemplateResult {
  return html`<textarea
    class="rlhf-seam et-answer"
    rows="1"
    ${seamAutoGrow()}
    placeholder=${placeholder}
    aria-label=${ariaLabel}
    lang="de"
  ></textarea>`
}

/** Read the trimmed value of an `.et-answer` open-text field from a container. */
export function readOpenText(root: Element | null | undefined): string {
  return root?.querySelector<HTMLTextAreaElement>("textarea.et-answer")?.value.trim() ?? ""
}

// ─── Selection reaction grids (.opt/.cell) — shared by both surfaces ─────────────

export interface AnswerOption {
  id: string
  label: string
  detail?: string
}

/** The structural shape every card carries — both surfaces' card types satisfy it. */
export interface AnswerCard {
  primitive: string
  prompt: string
  options?: AnswerOption[]
  optionA?: AnswerOption
  optionB?: AnswerOption
  axis?: DifferentialAxis
}

/** The per-card selection state (one instance per card; the interview keys these by block). */
export interface AnswerSelection {
  choiceId: string | null
  bestId: string | null
  worstId: string | null
  vetoes: readonly string[]
}

/** Resolved (already-t()'d) labels the grids render — each surface passes its own namespace. */
export interface AnswerLabels {
  choose: string
  best: string
  worst: string
  veto: string
  openPlaceholder: string
}

export const emptySelection = (): AnswerSelection => ({ choiceId: null, bestId: null, worstId: null, vetoes: [] })

/**
 * Recover the two poles of a `pairwise` card: `optionA`/`optionB` when present, else an
 * exactly-two `options[]` (the deployed field-migration on the read side). Anything else
 * is a degraded card.
 */
export function pairwisePoles(card: AnswerCard): AnswerOption[] {
  const both = [card.optionA, card.optionB].filter((o): o is AnswerOption => Boolean(o))
  if (both.length === 2) return both
  if (card.options?.length === 2) return card.options
  return []
}

/** A card whose primitive has nothing answerable to draw (needs the surface's degraded state). */
export function isCardDegraded(card: AnswerCard): boolean {
  switch (card.primitive) {
    case "pairwise":
      return pairwisePoles(card).length < 2
    case "best_worst":
    case "vignette":
    case "simulation":
      return (card.options?.length ?? 0) < 2
    case "semantic_differential":
      return !card.axis
    default:
      return false
  }
}

/** Is the current selection enough to CONFIRM this primitive? (open-text is read from the DOM.) */
export function isAnswerable(primitive: string, sel: AnswerSelection): boolean {
  switch (primitive) {
    case "pairwise":
    case "vignette":
    case "simulation":
      return sel.choiceId !== null
    case "best_worst":
      return sel.bestId !== null && sel.worstId !== null
    case "semantic_differential":
      return true
    default:
      return false
  }
}

interface LegendCol {
  cls: string
  label: string
}
function reactionLegend(cols: LegendCol[]): TemplateResult {
  return html`<div class="legend" aria-hidden="true">
    <span></span>
    <div class=${classMap({ "legend-cols": true, [`legend-cols-${cols.length}`]: true })}>
      ${cols.map((c) => html`<span class=${c.cls} lang="de">${c.label}</span>`)}
    </div>
  </div>`
}

const optMain = (o: AnswerOption): TemplateResult => html`<div class="opt-main">
  <p class="opt-label" lang="de">${o.label}</p>${o.detail ? html`<p class="opt-detail" lang="de">${o.detail}</p>` : nothing}
</div>`

/**
 * The answer INPUTS for a card (no confirm button — the surface adds that). Dispatches by
 * primitive to a `.opt`/`.cell` reaction grid, the slider, or the open-text seam. `id` is
 * the block/step id, stamped as `data-block` on every action so a multi-card surface can
 * route the pick. Assumes a non-degraded, answerable primitive (the surface gates
 * card_sort / degraded first).
 */
export function renderCardInputs(
  card: AnswerCard,
  opts: { id: string; sel: AnswerSelection; submitting: boolean; labels: AnswerLabels },
): TemplateResult {
  const { id, sel, submitting: dis, labels } = opts
  switch (card.primitive) {
    case "pairwise": {
      const poles = pairwisePoles(card)
      return html`
        ${reactionLegend([{ cls: "c-choose", label: labels.choose }])}
        <ul class="options" role="group">
          ${poles.map((o, i) => {
            const poleId = i === 0 ? "A" : "B"
            const chosen = sel.choiceId === poleId
            return html`<li class=${classMap({ opt: true, "is-chosen": chosen })}>
              ${optMain(o)}
              <div class="opt-cells opt-cells-1">
                <button class="cell k-choose" data-act="pick-option" data-role="choice" data-id=${poleId} data-block=${id} aria-pressed=${chosen ? "true" : "false"} aria-label=${o.label} ?disabled=${dis} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✓</span></button>
              </div>
            </li>`
          })}
        </ul>`
    }
    case "best_worst": {
      const options = card.options ?? []
      return html`
        ${reactionLegend([{ cls: "c-choose", label: labels.best }, { cls: "c-nogo", label: labels.worst }])}
        <ul class="options" role="group">
          ${options.map((o) => {
            const most = sel.bestId === o.id
            const least = sel.worstId === o.id
            return html`<li class=${classMap({ opt: true, "is-most": most, "is-least": least })}>
              ${optMain(o)}
              <div class="opt-cells opt-cells-2">
                <button class="cell k-most" data-act="pick-option" data-role="best" data-id=${o.id} data-block=${id} aria-pressed=${most ? "true" : "false"} aria-label=${labels.best} ?disabled=${dis} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✓</span></button>
                <button class="cell k-least" data-act="pick-option" data-role="worst" data-id=${o.id} data-block=${id} aria-pressed=${least ? "true" : "false"} aria-label=${labels.worst} ?disabled=${dis} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">▽</span></button>
              </div>
            </li>`
          })}
        </ul>`
    }
    case "vignette":
    case "simulation": {
      const options = card.options ?? []
      return html`
        ${reactionLegend([{ cls: "c-choose", label: labels.choose }, { cls: "c-nogo", label: labels.veto }])}
        <ul class="options" role="group">
          ${options.map((o) => {
            const chosen = sel.choiceId === o.id
            const vetoed = sel.vetoes.includes(o.id)
            return html`<li class=${classMap({ opt: true, "is-chosen": chosen, "is-nogo": vetoed })}>
              ${optMain(o)}
              <div class="opt-cells opt-cells-2">
                <button class="cell k-choose" data-act="pick-option" data-role="choice" data-id=${o.id} data-block=${id} aria-pressed=${chosen ? "true" : "false"} aria-label=${labels.choose} ?disabled=${dis} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✓</span></button>
                <button class="cell k-nogo" data-act="toggle-veto" data-id=${o.id} data-block=${id} aria-pressed=${vetoed ? "true" : "false"} aria-label=${labels.veto} ?disabled=${dis} lang="de"><span class="dot"></span><span class="glyph" aria-hidden="true">✕</span></button>
              </div>
            </li>`
          })}
        </ul>`
    }
    case "semantic_differential":
      return semanticDifferential(card.axis, card.prompt)
    default:
      return openTextField(labels.openPlaceholder, card.prompt)
  }
}
